/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { RELATIONSHIP_MODEL_VERSION } from '../constants';
import type {
  JoinType,
  LegacyVqbEdge,
  LegacyVqbNode,
  LegacyVqbNodeInfo,
  ModelNode,
  ModelRelationship,
  PersistedModelEnvelope,
  RelationshipModel,
} from '../types';

const JOIN_TYPE_MAP: Record<string, JoinType> = {
  INNER: 'inner',
  LEFT: 'left',
  RIGHT: 'right',
  'FULL OUTER': 'full',
};

function normalizeSelectedFields(
  selectedFields: string[] | Set<string> | undefined,
): string[] | undefined {
  if (!selectedFields) {
    return undefined;
  }
  const arr = Array.isArray(selectedFields)
    ? selectedFields
    : Array.from(selectedFields);
  return arr.length > 0 ? arr : undefined;
}

function parseTableNodeId(
  nodeId: string,
): { schema: string; table: string } | null {
  const match = /^table-(.+)-([^-].*)$/.exec(nodeId);
  if (!match) {
    return null;
  }
  return { schema: match[1], table: match[2] };
}

function legacyNodeToModelNode(node: LegacyVqbNode): ModelNode | null {
  const schema = node.data?.schema ?? parseTableNodeId(node.id)?.schema ?? '';
  const table =
    node.data?.tableName ?? parseTableNodeId(node.id)?.table ?? node.id;

  if (!table) {
    return null;
  }

  return {
    id: node.id,
    kind: 'physical_table',
    ref: { schema, table },
    outputColumns: normalizeSelectedFields(node.data?.selectedFields),
    position: node.position ?? { x: 0, y: 0 },
    customAlias: node.data?.customAlias,
  };
}

function legacyEdgeToRelationship(
  edge: LegacyVqbEdge,
): ModelRelationship | null {
  const sourceField = edge.data?.sourceField;
  const targetField = edge.data?.targetField;
  if (!sourceField || !targetField) {
    return null;
  }

  const joinType =
    JOIN_TYPE_MAP[edge.data?.joinType ?? 'INNER'] ?? ('inner' as JoinType);

  return {
    id: edge.id,
    leftNodeId: edge.source,
    rightNodeId: edge.target,
    joinType,
    keys: [{ leftColumn: sourceField, rightColumn: targetField }],
  };
}

export function migrateVqbNodeInfoToModel(
  nodeInfo: unknown,
): RelationshipModel | null {
  if (!nodeInfo || typeof nodeInfo !== 'object') {
    return null;
  }

  const envelope = nodeInfo as PersistedModelEnvelope;
  if (
    envelope.version === RELATIONSHIP_MODEL_VERSION &&
    envelope.relationshipModel
  ) {
    return envelope.relationshipModel;
  }

  const legacy = nodeInfo as LegacyVqbNodeInfo;
  if (!Array.isArray(legacy.nodes) || legacy.nodes.length === 0) {
    return null;
  }

  const nodes: ModelNode[] = legacy.nodes
    .map(legacyNodeToModelNode)
    .filter((n): n is ModelNode => n !== null);

  if (nodes.length === 0) {
    return null;
  }

  const relationships: ModelRelationship[] = (legacy.edges ?? [])
    .map(legacyEdgeToRelationship)
    .filter((r): r is ModelRelationship => r !== null);

  return {
    version: RELATIONSHIP_MODEL_VERSION,
    nodes,
    relationships,
    filterBindings: [],
  };
}

export function wrapModelInEnvelope(
  model: RelationshipModel,
  uiPrefs?: PersistedModelEnvelope['uiPrefs'],
  legacyVqb?: unknown,
): PersistedModelEnvelope {
  return {
    version: RELATIONSHIP_MODEL_VERSION,
    relationshipModel: model,
    uiPrefs,
    ...(legacyVqb !== undefined ? { _legacyVqb: legacyVqb } : {}),
  };
}
